import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Close as CloseIcon } from '@mui/icons-material';
import ChildFriendlyOutlinedIcon from '@mui/icons-material/ChildFriendlyOutlined';
import { useMediaQuery } from '@mui/material';
import { ChevronLeft as ChevronLeftIcon } from '@mui/icons-material';

const RoomSelector = React.forwardRef(({
  initialRooms = [{
    adult: 2,
    childAges: []
  }],
  onChange,
  readOnly = false,
  autoOpen = false,
  forceOpen = false,
  onDone,
  onFocus,
  onBlur,
  translation,
  onDropdownToggle = null
}, ref) => {
  const isMobile = useMediaQuery('(max-width:768px)');
  const [localRooms, setLocalRooms] = useState(initialRooms);
  const [isDropdownOpen, setIsDropdownOpen] = useState(autoOpen);
  const internalRef = useRef(null);
  const wrapperRef = ref || internalRef;

  // Call onDropdownToggle when dropdown state changes
  useEffect(() => {
    if (onDropdownToggle) {
      onDropdownToggle(isDropdownOpen);
    }
  }, [isDropdownOpen, onDropdownToggle]);

  // Handle forceOpen prop
  useEffect(() => {
    if (forceOpen && !isDropdownOpen) {
      setIsDropdownOpen(true);
    } else if (forceOpen && isDropdownOpen) {
    }
  }, [forceOpen, isDropdownOpen]);

  // Memoize update functions to prevent re-renders
  const updateAdults = useCallback((index, value) => {
    setLocalRooms(prev => {
      const newRooms = [...prev];
      newRooms[index] = { 
        ...newRooms[index], 
        adult: Math.max(1, Math.min(6, value)) 
      };
      // Trigger onChange immediately
      onChange?.(newRooms);
      return newRooms;
    });
  }, [onChange]);

  const updateChildren = useCallback((index, value) => {
    setLocalRooms(prev => {
      const newRooms = [...prev];
      const currentLength = newRooms[index].childAges?.length || 0;
      
      if (value > currentLength) {
        newRooms[index].childAges = [
          ...(newRooms[index].childAges || []),
          ...Array(value - currentLength).fill(5)
        ];
      } else if (value < currentLength) {
        newRooms[index].childAges = newRooms[index].childAges.slice(0, value);
      }
      
      // Trigger onChange immediately
      onChange?.(newRooms);
      return newRooms;
    });
  }, [onChange]);

  const updateChildAge = useCallback((roomIndex, childIndex, age) => {
    setLocalRooms(prev => {
      const newRooms = [...prev];
      newRooms[roomIndex].childAges[childIndex] = age;
      // Trigger onChange immediately
      onChange?.(newRooms);
      return newRooms;
    });
  }, [onChange]);

  const addRoom = useCallback(() => {
    setLocalRooms(prev => {
      if (prev.length < 5) {
        const newRooms = [...prev, { adult: 2, childAges: [] }];
        // Trigger onChange immediately
        onChange?.(newRooms);
        return newRooms;
      }
      return prev;
    });
  }, [onChange]);

  const removeRoom = useCallback((index) => {
    setLocalRooms(prev => {
      if (prev.length > 1) {
        const newRooms = [...prev];
        newRooms.splice(index, 1);
        // Trigger onChange immediately
        onChange?.(newRooms);
        return newRooms;
      }
      return prev;
    });
  }, [onChange]);

  // Handle done action
  const handleDone = useCallback(() => {
    // Just trigger onDone since changes are already synced
    setIsDropdownOpen(false);
    onDone?.(localRooms);
  }, [localRooms, onDone]);

  // Handle opening dropdown
  const handleOpenDropdown = useCallback(() => {
    if (!readOnly) {
      setIsDropdownOpen(true);
      if (onFocus) onFocus();
    }
  }, [readOnly, onFocus]);

  // Re-initialize rooms when initialRooms changes
  useEffect(() => {
    setLocalRooms(initialRooms);
  }, [initialRooms]);

  // Set initial dropdown state
  useEffect(() => {
    setIsDropdownOpen(autoOpen);
  }, [autoOpen]);

  // Format display text - memoized to prevent re-renders
  const displayText = useMemo(() => {
    const totalAdults = localRooms.reduce((sum, room) => sum + room.adult, 0);
    const totalChildren = localRooms.reduce((sum, room) => sum + (room.childAges?.length || 0), 0);
    const totalPeople = totalAdults + totalChildren;
    
    const roomsText = localRooms.length > 1 ? (translation?.rooms || 'Rooms') : (translation?.room || 'Room');
    const personsText = totalPeople > 1 ? (translation?.persons || 'Persons') : (translation?.person || 'Person');
    const adultsText = totalAdults > 1 ? (translation?.adults || 'Adults') : (translation?.adult || 'Adult');
    const childrenText = totalChildren > 1 ? (translation?.children || 'Children') : (translation?.child || 'Child');
    
    return `${localRooms.length} ${roomsText} | ${totalPeople} ${personsText}${
      totalChildren > 0 ? ` (${totalAdults} ${adultsText}, ${totalChildren} ${childrenText})` : ''
    }`;
  }, [localRooms, translation]);

  return (
    <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
      {!autoOpen && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          background: "#f8f9fa",
          borderRadius: '8px',
          height: '56px',
        }}>
          <div style={{
            paddingLeft: '15px',
            display: 'flex',
            alignItems: 'center'
          }}>
            <img 
              src={require("../../assets/svg/personOutLine.svg").default}
              alt="person"
              style={{ width: 24, height: 24 }} 
            />
          </div>
          <input
            id="room-selector-input"
            type="text"
            className="form-control"
            placeholder={displayText}
            value={displayText}
            readOnly
            onClick={handleOpenDropdown}
            style={{
              padding: "12px 16px",
              paddingLeft: "8px",
              border: 'none',
              background: "transparent",
              cursor: readOnly ? "default" : "pointer",
              height: '56px',
              fontSize: '18px',
              fontWeight: '500'
            }}
          />
        </div>
      )}

      {(autoOpen || isDropdownOpen) && (
        <div style={{ 
          flex: 1, 
          overflowY: 'auto',
          padding: 0,
          ...(autoOpen ? {} : {
            position: 'absolute',
            top: 'calc(100% + 8px)',
            left: 0,
            width: '100%',
            backgroundColor: '#fff',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            zIndex: 1000,
            maxHeight: '80vh'
          })
        }}>
          <RoomSelectorContent
            rooms={localRooms}
            addRoom={addRoom}
            removeRoom={removeRoom}
            updateAdults={updateAdults}
            updateChildren={updateChildren}
            updateChildAge={updateChildAge}
            translation={translation}
          />
          <div style={{
            padding: '16px',
            borderTop: '1px solid #eef1f5',
            backgroundColor: 'white'
          }}>
            <button
              onClick={handleDone}
              style={{
                width: '100%',
                padding: '14px',
                backgroundColor: '#FBD316',
                color: '#003663',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              {translation?.done || "Done"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

RoomSelector.displayName = 'RoomSelector';

// RoomSelectorContent component with memoization to prevent re-renders
const RoomSelectorContent = React.memo(({
  rooms,
  addRoom,
  removeRoom,
  updateAdults,
  updateChildren,
  updateChildAge,
  translation
}) => {
  // Style definitions
  const styles = {
    counterStyle: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "16px 20px",
      borderBottom: "1px solid #eef1f5",
      width: "100%",
    },
    labelStyle: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    controlsStyle: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    numberStyle: {
      minWidth: "24px",
      textAlign: "center",
      fontSize: "16px",
      color: "#003663",
      fontWeight: "500",
    },
    circleBtn: {
      width: 32,
      height: 32,
      minWidth: '32px',
      minHeight: '32px',
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 18,
      cursor: "pointer",
      border: "none",
      borderRadius: "50%",
      backgroundColor: "#e9ecef",
      color: "#003663",
      transition: "all 0.2s ease",
      transform: "scale(1)",
      padding: '0',
      lineHeight: '1',
      boxSizing: 'border-box'
    },
    activeCircleBtn: {
      backgroundColor: "#FBD316",
    },
    disabledCircleBtn: {
      opacity: 0.5,
      cursor: "not-allowed",
      backgroundColor: "#e9ecef"
    }
  };

  return (
    <div style={{ padding: 0 }}>
      {rooms.map((room, roomIndex) => (
        <div key={roomIndex}>
          {rooms.length > 1 && (
            <div style={{ 
              padding: "8px 20px",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center"
            }}>
              <button
                onClick={() => removeRoom(roomIndex)}
                style={{
                  border: "none",
                  background: "none",
                  color: "#FF3B30",
                  cursor: "pointer",
                  padding: "4px 8px",
                  fontSize: "14px"
                }}
              >
                {translation?.remove || "Remove"}
              </button>
            </div>
          )}

          {/* Adults */}
          <div style={styles.counterStyle}>
            <div style={styles.labelStyle}>
              <img 
                src={require("../../assets/svg/personInline.svg").default}
                alt="adult"
                style={{ width: 24, height: 24 }} 
              />
              <div>
                <div style={{ fontWeight: 500, color: '#003663' }}>{translation?.adults || "Adults"}</div>
                <div style={{ fontSize: '14px', color: '#666' }}>{translation?.["12_years_and_older"] || "12 years and older"}</div>
              </div>
            </div>
            <div style={styles.controlsStyle}>
              <button
                style={{
                  ...styles.circleBtn,
                  ...(room.adult <= 1 ? styles.disabledCircleBtn : styles.activeCircleBtn)
                }}
                onClick={() => updateAdults(roomIndex, room.adult - 1)}
                disabled={room.adult <= 1}
              >
                -
              </button>
              <span style={styles.numberStyle}>{room.adult}</span>
              <button
                style={{
                  ...styles.circleBtn,
                  ...(room.adult >= 6 ? styles.disabledCircleBtn : styles.activeCircleBtn)
                }}
                onClick={() => updateAdults(roomIndex, room.adult + 1)}
                disabled={room.adult >= 6}
              >
                +
              </button>
            </div>
          </div>

          {/* Children */}
          <div style={styles.counterStyle}>
            <div style={styles.labelStyle}>
              <img 
                src={require("../../assets/svg/personInline.svg").default}
                alt="child"
                style={{ width: 24, height: 24 }} 
              />
              <div>
                <div style={{ fontWeight: 500, color: '#003663' }}>{translation?.children || "Children"}</div>
                <div style={{ fontSize: '14px', color: '#666' }}>{translation?.["2_to_11_years"] || "2 to 11 years"}</div>
              </div>
            </div>
            <div style={styles.controlsStyle}>
              <button
                style={{
                  ...styles.circleBtn,
                  ...((room.childAges?.length || 0) <= 0 ? styles.disabledCircleBtn : styles.activeCircleBtn)
                }}
                onClick={() => updateChildren(roomIndex, (room.childAges?.length || 0) - 1)}
                disabled={(room.childAges?.length || 0) <= 0}
              >
                -
              </button>
              <span style={styles.numberStyle}>{room.childAges?.length || 0}</span>
              <button
                style={{
                  ...styles.circleBtn,
                  ...((room.childAges?.length || 0) >= 4 ? styles.disabledCircleBtn : styles.activeCircleBtn)
                }}
                onClick={() => updateChildren(roomIndex, (room.childAges?.length || 0) + 1)}
                disabled={(room.childAges?.length || 0) >= 4}
              >
                +
              </button>
            </div>
          </div>

          {/* Child Ages */}
          {room.childAges?.length > 0 && (
            <div style={{ padding: '16px 20px' }}>
              <div style={{ fontWeight: 500, color: '#003663', marginBottom: '12px' }}>
                {translation?.children_ages || "Children ages"}
              </div>
              {room.childAges.map((age, childIndex) => (
                <div key={childIndex} style={{ marginBottom: '8px' }}>
                  <select
                    value={age}
                    onChange={(e) => updateChildAge(roomIndex, childIndex, parseInt(e.target.value, 10))}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #eef1f5',
                      backgroundColor: '#f8f9fa',
                      color: '#003663',
                      fontSize: '14px'
                    }}
                  >
                    {[2,3,4,5,6,7,8,9,10,11].map((opt) => (
                      <option key={opt} value={opt}>
                        {opt} {translation?.years_old || "years old"}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {rooms.length < 5 && (
        <div style={{ padding: '16px 20px' }}>
          <button
            onClick={addRoom}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: '#f8f9fa',
              color: '#003663',
              border: '1px dashed #003663',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            + {translation?.add_a_room || "Add a room"}
          </button>
        </div>
      )}
    </div>
  );
});

export default RoomSelector; 